import { useState, useEffect, useRef } from 'react';
import './VideoCarousel.css';

const ThumbnailItem = ({ video, isActive, onClick, index }) => {
    const videoRef = useRef(null);
    const [isVideoReady, setIsVideoReady] = useState(false);

    useEffect(() => {
        if (isActive && videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play().catch(() => {});
        } else {
            setIsVideoReady(false);
        }
    }, [isActive]);

    return (
        <div 
            className={`thumbnail ${isActive ? 'active' : ''}`} 
            onClick={() => onClick(index)}
            data-index={index}
        >
            <div className="thumbnail-image">
                <img 
                    src={video.thumbnail} 
                    alt="thumb" 
                    className="thumbnail-image-static"
                    style={{ 
                        opacity: (isActive && isVideoReady) ? 0 : 1, // Use opacity instead of display
                        transition: 'opacity 0.3s ease',
                        position: 'absolute',
                        top: 0, left: 0, zIndex: 1
                    }}
                />
                
                {isActive && video.thumbnailVideo && (
                    <video 
                        ref={videoRef} 
                        src={video.thumbnailVideo} 
                        muted loop playsInline 
                        poster={video?.thumbnail}
                        className="thumbnail-video"
                        onCanPlayThrough={() => setIsVideoReady(true)}
                        style={{ position: 'relative', zIndex: 2 }}
                    />
                )}
            </div>
        </div>
    );
};

const VideoCarousel = ({ videos }) => {
    const originalCount = videos.length;
    const infiniteVideos = [...videos, ...videos, ...videos];
    const [activeIndex, setActiveIndex] = useState(originalCount);
    const scrollContainerRef = useRef(null);
    const isResetting = useRef(false);

    // SEAMLESS TELEPORTATION
    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container || isResetting.current) return;

        const { scrollLeft, scrollWidth } = container;
        const segmentWidth = scrollWidth / 3;

        if (scrollLeft < segmentWidth * 0.5) {
            isResetting.current = true;
            container.style.scrollBehavior = 'auto';
            container.scrollLeft = scrollLeft + segmentWidth;
            requestAnimationFrame(() => {
                container.style.scrollBehavior = 'smooth';
                isResetting.current = false;
            });
        } else if (scrollLeft > segmentWidth * 1.5) {
            isResetting.current = true;
            container.style.scrollBehavior = 'auto';
            container.scrollLeft = scrollLeft - segmentWidth;
            requestAnimationFrame(() => {
                container.style.scrollBehavior = 'smooth';
                isResetting.current = false;
            });
        }
    };

    useEffect(() => {
        // Preload videos
        videos.slice(0, 3).forEach((v) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'video';
            link.href = v.url;
            document.head.appendChild(link);
        });

        // Preload thumbnails
        videos.forEach((v) => {
            const img = new Image();
            img.src = v.thumbnail;
        });
    }, [videos]);

    // ACTIVE ITEM DETECTION (Simplified)
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const updateActiveIndex = () => {
            if (isResetting.current) return;
            
            const containerRect = container.getBoundingClientRect();
            const containerCenter = containerRect.left + containerRect.width / 2;
            
            let closestIndex = 0;
            let minDistance = Infinity;
            
            const thumbnails = container.querySelectorAll('.thumbnail');
            thumbnails.forEach((thumb) => {
                const thumbRect = thumb.getBoundingClientRect();
                const thumbCenter = thumbRect.left + thumbRect.width / 2;
                const distance = Math.abs(thumbCenter - containerCenter);
                
                if (distance < minDistance) {
                    minDistance = distance;
                    closestIndex = parseInt(thumb.getAttribute('data-index'));
                }
            });
            
            setActiveIndex(closestIndex);
        };

        // Use a throttled scroll handler
        let scrollTimeout;
        const handleScrollThrottled = () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(updateActiveIndex, 100);
        };

        container.addEventListener('scroll', handleScrollThrottled);
        
        // Initial update after a delay
        setTimeout(updateActiveIndex, 300);
        
        return () => {
            container.removeEventListener('scroll', handleScrollThrottled);
            if (scrollTimeout) clearTimeout(scrollTimeout);
        };
    }, [infiniteVideos.length]);

    // INITIAL POSITIONING (with better timing)
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Wait for DOM to be ready
        const initTimer = setTimeout(() => {
            container.style.scrollBehavior = 'auto';
            const segmentWidth = container.scrollWidth / 3;
            container.scrollLeft = segmentWidth;
            container.style.scrollBehavior = 'smooth';
            
            // Set active index to middle of middle segment
            const middleIndex = originalCount + Math.floor(originalCount / 2);
            setActiveIndex(middleIndex);
        }, 100);
        
        return () => clearTimeout(initTimer);
    }, [originalCount]);

    // CLICK TO CENTER
    const handleThumbnailClick = (index) => {
        const container = scrollContainerRef.current;
        const target = container.querySelectorAll('.thumbnail')[index];
        
        if (target && container) {
            const containerWidth = container.offsetWidth;
            const targetLeft = target.offsetLeft;
            const targetWidth = target.offsetWidth;
            const scrollTo = targetLeft - (containerWidth / 2) + (targetWidth / 2);

            container.scrollTo({ left: scrollTo, behavior: 'smooth' });
        }
    };

    return (
        <div className="video-carousel">
            <div className="main-video-container">
                <video 
                    key={videos[activeIndex % originalCount]?.url} 
                    autoPlay muted loop playsInline preload='auto'
                    className="video-player"
                >
                    <source src={videos[activeIndex % originalCount]?.url} type="video/mp4" />
                </video>
            </div>

            <div className="thumbnails-wrapper">
                <div 
                    className="thumbnails-container" 
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                >
                    <div className="thumbnails">
                        {infiniteVideos.map((v, i) => (
                            <ThumbnailItem 
                                key={i} 
                                index={i}
                                video={v} 
                                isActive={i === activeIndex} 
                                onClick={handleThumbnailClick} 
                            />
                        ))}
                    </div>
                </div>
            </div>
            <p className="swipe-text">swipe for more</p>
        </div>
    );
};

export default VideoCarousel;